import React, { useEffect, useState } from "react";
import { Counterparty } from "@common/types/counterparty";
import { useCounterpartyByIdQuery } from "@api/counterparty.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useOverlays } from "contexts/OverlayContext";
import { CounterpartyEditorDrawer } from "./CounterpartyEditorDrawer";

interface CounterpartyEditorOverlayProps {
    counterpartyId: string;
}

const CounterpartyEditorOverlay: React.FC<CounterpartyEditorOverlayProps> = ({
    counterpartyId,
}) => {
    const { closeOverlay, overlayIsClosing } = useOverlays();
    const { error: toastError } = useToaster();
    // Editing contacts/addresses on a global counterparty copies it into the
    // workspace first; follow the local copy so later reads show saved edits.
    const [localizedCounterparty, setLocalizedCounterparty] =
        useState<Counterparty>();

    const counterpartyQuery = useCounterpartyByIdQuery({
        counterpartyId,
        enabled: true,
    });
    const isUnavailable =
        counterpartyQuery.isError ||
        (counterpartyQuery.isSuccess && !counterpartyQuery.data);

    useEffect(() => {
        if (isUnavailable) {
            toastError("Failed to load counterparty");
            closeOverlay();
        }
    }, [isUnavailable, toastError, closeOverlay]);

    const counterparty = localizedCounterparty ?? counterpartyQuery.data;

    return (
        <CounterpartyEditorDrawer
            open={!overlayIsClosing && Boolean(counterparty)}
            isCreating={false}
            counterparty={counterparty}
            onClose={closeOverlay}
            onCreated={() => undefined}
            onEdited={closeOverlay}
            onLocalized={setLocalizedCounterparty}
        />
    );
};

/**
 * Global overlay for opening the counterparty editor from anywhere (e.g. chat
 * counterparty reference chips) given only a counterparty id.
 */
export const CounterpartyEditorDrawerWrapper: React.FC = () => {
    const { getOverlayInfo } = useOverlays();
    const overlayInfo = getOverlayInfo("counterpartyEditorDrawer");
    const counterpartyId = overlayInfo?.data?.counterpartyId;

    if (!counterpartyId) {
        return null;
    }

    return (
        <CounterpartyEditorOverlay
            key={counterpartyId}
            counterpartyId={counterpartyId}
        />
    );
};
